import React, { useState, useRef, useEffect, TouchEvent, MouseEvent } from 'react';
import { Image, OffMarketWillingnessEvent as OffMarketWillingnessEventType } from '../utils/types';
import OffMarketSellIcon from './OffMarketSellIcon';
import OffMarketBuyIcon from './OffMarketBuyIcon';
import { Trans, useTranslation } from 'react-i18next';
import './FeedComments.scss';
import Linkify from 'linkify-react';
import DOMPurify from 'dompurify';
import ZoomIcon from '../BuildingModal/Comments/ZoomIcon';
import MultiImageModal from '../ImageModal/MultiImageModal';
import MiniMenuIcon from './MiniMenuIcon';
import OffMarketBuyAndSellDetailsModal, { PropertyDetails } from '../BuildingModal/OffMarketBuyAndSellDetailsModal';
import { getCookie } from '../utils/utils';
import '../MessagesModal/MessagesModal.scss';
import TrashIcon from '../BuildingModal/Comments/TrashIcon';
import { CloseIcon } from '../common/CloseIcon';
import TagManager from 'react-gtm-module';

type OffMarketWillingnessEventProps = {
  offMarketWillingnessEvent: OffMarketWillingnessEventType;
  handleFeedEventClick?: (comment: OffMarketWillingnessEventType) => void;
  setShowImageModal?: (imageUrl: string | null) => void;
  isLoggedIn?: boolean;
  fetchFeed?: (loadMore?: boolean) => void;
  handleRefreshFeedClick: () => void;
  fetchComments?: (isVoteRefresh: boolean) => void;
  couldSellSelected?: boolean;
  couldBuySelected?: boolean;
  setCouldSellSelected?: (selected: boolean) => void;
  setCouldBuySelected?: (selected: boolean) => void;
};

// Image Carousel Component
const ImageCarousel = ({ images, isBuildingModal, setShowImageModal }: { images: Array<Image>, isBuildingModal: boolean, setShowImageModal?: (imageUrl: string | null) => void }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [prevTranslate, setPrevTranslate] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragEnabled, setIsDragEnabled] = useState(false);
  const [showMultiImageModal, setShowMultiImageModal] = useState(false);

  // Check screen size on mount and on resize
  useEffect(() => {
    const checkScreenSize = () => {
      setIsDragEnabled(window.innerWidth < 500);
    };

    // Initial check
    checkScreenSize();

    // Add resize listener
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Calculate which dots should be visible (maximum 5)
  const MAX_VISIBLE_DOTS = 5;
  const totalDots = images.length;

  // Calculate which dot should be centered in the visible window
  const calculateVisibleDotsStart = (activeIndex: number) => {
    if (totalDots <= MAX_VISIBLE_DOTS) {
      // If we have 5 or fewer dots, show all from the beginning
      return 0;
    }

    // Middle position in the window
    const middle = Math.floor(MAX_VISIBLE_DOTS / 2);

    // If we're at the beginning, show first MAX_VISIBLE_DOTS dots
    if (activeIndex <= middle) {
      return 0;
    }

    // If we're at the end, show last MAX_VISIBLE_DOTS dots
    if (activeIndex >= totalDots - middle - 1) {
      return totalDots - MAX_VISIBLE_DOTS;
    }

    // Otherwise, center the active dot
    return activeIndex - middle;
  };

  const visibleDotsStart = calculateVisibleDotsStart(currentIndex);

  const showArrows = images.length > 1;

  // Handle opening the multi-image modal
  const openMultiImageModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    setTimeout(() => {
      setShowMultiImageModal(true);
    }, 0);
  };

  // Handle navigation
  const goToNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const goToPrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Go to specific image
  const goToImage = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(index);
  };

  // Touch handling for drag events
  const handleTouchStart = (e: TouchEvent) => {
    if (!isDragEnabled) return;

    setIsDragging(true);
    setStartPosition(e.touches[0].clientX);
    setPrevTranslate(currentTranslate);
    setDragOffset(0);
    e.stopPropagation();
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragEnabled || !isDragging) return;

    const currentPosition = e.touches[0].clientX;
    const diff = currentPosition - startPosition;

    // Prevent dragging right when at the first image or dragging left when at the last image
    if ((currentIndex === 0 && diff > 0) || (currentIndex === images.length - 1 && diff < 0)) {
      return;
    }

    // Apply a multiplier to make small movements more significant
    setCurrentTranslate(prevTranslate + diff * 1.5);
    setDragOffset(diff * 1.5);
    e.stopPropagation();
  };

  const handleTouchEnd = (e: TouchEvent | MouseEvent) => {
    if (!isDragEnabled) return;

    setIsDragging(false);
    const threshold = 40; // reduced from 100 to 40 to increase sensitivity

    if (Math.abs(currentTranslate - prevTranslate) > threshold) {
      if (currentTranslate > prevTranslate && currentIndex > 0) {
        // Swiped right - go to previous
        setCurrentIndex(currentIndex - 1);
      } else if (currentTranslate < prevTranslate && currentIndex < images.length - 1) {
        // Swiped left - go to next
        setCurrentIndex(currentIndex + 1);
      }
    }

    // Reset translate values
    setCurrentTranslate(0);
    setPrevTranslate(0);
    setDragOffset(0);
    e.stopPropagation();
  };

  // Mouse handling for drag events
  const handleMouseDown = (e: MouseEvent) => {
    if (!isDragEnabled) return;

    setIsDragging(true);
    setStartPosition(e.clientX);
    setPrevTranslate(currentTranslate);
    setDragOffset(0);
    e.stopPropagation();

    document.addEventListener('mousemove', handleMouseMove as unknown as EventListener);
    document.addEventListener('mouseup', handleMouseUp as unknown as EventListener);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragEnabled || !isDragging) return;

    const currentPosition = e.clientX;
    const diff = currentPosition - startPosition;

    // Prevent dragging right when at the first image or dragging left when at the last image
    if ((currentIndex === 0 && diff > 0) || (currentIndex === images.length - 1 && diff < 0)) {
      return;
    }

    // Apply a multiplier to make small movements more significant
    setCurrentTranslate(prevTranslate + diff * 1.5);
    setDragOffset(diff * 1.5);
    e.stopPropagation();
  };

  const handleMouseUp = (e: MouseEvent) => {
    document.removeEventListener('mousemove', handleMouseMove as unknown as EventListener);
    document.removeEventListener('mouseup', handleMouseUp as unknown as EventListener);
    handleTouchEnd(e);
  };

  // Cleanup event listeners
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove as unknown as EventListener);
      document.removeEventListener('mouseup', handleMouseUp as unknown as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  return (
    <>
      <div
        className="image-carousel"
        ref={carouselRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
      >
        {/* Image Counter */}
        {images.length > 1 && (
          <div className="image-carousel__counter">
            {currentIndex + 1}/{images.length}
          </div>
        )}

        {/* Images */}
        <div className="image-carousel__image-container">
          {images.map((image, index) => (
            <img
              key={index}
              src={isBuildingModal ? image.url : image.thumbnailUrl}
              alt={`Property ${index + 1}`}
              loading="lazy"
              className={`image-carousel__image ${index === currentIndex ? 'image-carousel__image--active' : ''} ${isBuildingModal ? 'image-carousel__image--building-modal' : ''}`}
              style={{
                transform: `translateX(${index === currentIndex ?
                  (dragOffset / 5) : // Current image moves slightly with drag
                  100 * (index - currentIndex) + (dragOffset / 3) // Other images move more with drag
                  }%)`
              }}
              onClick={(e) => {
                if (isBuildingModal) {
                  e.stopPropagation();
                  openMultiImageModal(e);
                }
              }}
            />
          ))}

          {/* Zoom button for building modal */}
          {isBuildingModal && images.length > 0 && (
            <div className="image-carousel__zoom-button-wrapper">
              <button
                className="image-carousel__zoom-button"
                onClick={openMultiImageModal}
              >
                <ZoomIcon />
              </button>
            </div>
          )}
        </div>

        {/* Navigation Arrows */}
        {showArrows && (
          <>
            <button
              className={`image-carousel__arrow image-carousel__arrow--left ${currentIndex === 0 ? 'image-carousel__arrow--disabled' : ''}`}
              onClick={goToPrev}
              disabled={currentIndex === 0}
            >
              ‹
            </button>
            <button
              className={`image-carousel__arrow image-carousel__arrow--right ${currentIndex === images.length - 1 ? 'image-carousel__arrow--disabled' : ''}`}
              onClick={goToNext}
              disabled={currentIndex === images.length - 1}
            >
              ›
            </button>
          </>
        )}

        {/* Indicator Dots with sliding window */}
        {images.length > 1 && (
          <div className="image-carousel__indicators-container">
            <div
              className="image-carousel__indicators"
              style={{
                transform: `translateX(${totalDots <= MAX_VISIBLE_DOTS ? 0 : -visibleDotsStart * 15}px)`
              }}
            >
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`image-carousel__indicator ${index === currentIndex ? 'image-carousel__indicator--active' : ''}`}
                  onClick={(e) => goToImage(index, e)}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Multi-Image Modal */}
      {showMultiImageModal && (
        <MultiImageModal
          images={images}
          closeImageModal={() => setShowMultiImageModal(false)}
        />
      )}
    </>
  );
};

export const OffMarketWillingnessEvent = ({
  offMarketWillingnessEvent,
  handleFeedEventClick,
  setShowImageModal,
  isLoggedIn,
  fetchFeed,
  handleRefreshFeedClick,
  fetchComments,
  couldSellSelected,
  couldBuySelected,
  setCouldSellSelected,
  setCouldBuySelected,
}: OffMarketWillingnessEventProps) => {
  const { t } = useTranslation();
  const isBuildingModal = handleFeedEventClick === undefined;
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showEventControlsMenu, setShowEventControlsMenu] = React.useState<
    string | null
  >(null);
  const editEventRef =
    React.useRef<HTMLButtonElement | null>(null);
  const eventControlsMenuButtonRef = React.useRef<HTMLButtonElement | null>(
    null,
  );
  const eventControlsMenuRef = React.useRef<HTMLDivElement | null>(null);

  // State for edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const [eventData, setEventData] = useState<PropertyDetails | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isCouldSellMessage = offMarketWillingnessEvent.could_sell;
  const shortAddress =
    offMarketWillingnessEvent.address.street +
    (offMarketWillingnessEvent.address.houseNumber === 'Unknown'
      ? ''
      : ' ' + offMarketWillingnessEvent.address.houseNumber) +
    ', ' +
    offMarketWillingnessEvent.address.city;

  // Check if there are any property details to display
  const hasPropertyDetails = offMarketWillingnessEvent.property_description ||
    offMarketWillingnessEvent.property_size ||
    offMarketWillingnessEvent.property_rooms ||
    offMarketWillingnessEvent.property_floor ||
    offMarketWillingnessEvent.property_has_elevator === true ||
    offMarketWillingnessEvent.property_has_balcony === true ||
    offMarketWillingnessEvent.property_renovation_status;

  // Function to render renovation status as readable text
  const getRenovationStatusText = (status: string) => {
    switch (status) {
      case 'recently_renovated':
        return t('building.offMarket.modal.recentlyRenovated');
      case 'good_condition':
        return t('building.offMarket.modal.goodCondition');
      case 'needs_renovation':
        return t('building.offMarket.modal.needsRenovation');
      default:
        return status;
    }
  };

  // Handler for fetching and opening edit modal
  const handleEditClick = async () => {
    try {
      setEditModalLoading(true);

      // Fetch the current willingness data from the backend
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/off-market-willingness/${offMarketWillingnessEvent._id}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch willingness data');
      }

      const data = await response.json();

      // Convert the backend data to PropertyDetails format
      const propertyDetails: PropertyDetails = {
        description: data.property_description,
        size: data.property_size,
        rooms: data.property_rooms,
        floor: data.property_floor,
        hasElevator: data.property_has_elevator,
        hasBalcony: data.property_has_balcony,
        renovationStatus: data.property_renovation_status,
        images: data.images
      };

      setEventData(propertyDetails);
      setIsEditModalOpen(true);
      setShowEventControlsMenu(null); // Hide the menu
    } catch (error) {
      console.error('Error fetching willingness data:', error);
      alert(t('common.error', 'An error occurred. Please try again.'));
    } finally {
      setEditModalLoading(false);
    }
  };

  // Handler for updating the willingness event
  const handleEditConfirm = async (propertyDetails?: PropertyDetails) => {
    try {
      setEditModalLoading(true);
      console.log('propertyDetails', propertyDetails);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/off-market-willingness/${offMarketWillingnessEvent._id}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: JSON.stringify({
            property_description: propertyDetails?.description,
            // Send explicit nulls for cleared fields so backend can clear stored values
            property_size: propertyDetails?.size ?? null,
            property_rooms: propertyDetails?.rooms === '' ? null : propertyDetails?.rooms,
            property_floor: propertyDetails?.floor ?? null,
            property_has_elevator: propertyDetails?.hasElevator,
            property_has_balcony: propertyDetails?.hasBalcony,
            property_renovation_status: propertyDetails?.renovationStatus === '' ? null : propertyDetails?.renovationStatus,
            property_images: propertyDetails?.images
          }),
        }
      );

      if (!response.ok) {
        try {
          const err = await response.json();
          if (err && err.error_code === 'SIZE_MUST_BE_GREATER_THAN_ZERO') {
            alert(t('building.offMarket.modal.sizeMustBeGreaterThanZero'));
            return;
          }
        } catch (_) {
          // ignore json parse errors, fall back to generic
        }
        throw new Error('Failed to update willingness');
      }

      // Close the modal and refresh the page or update the local state
      setIsEditModalOpen(false);

      fetchComments?.(false);
      fetchFeed?.();
      handleRefreshFeedClick();
    } catch (error) {
      console.error('Error updating willingness:', error);
      alert(t('common.error', 'An error occurred. Please try again.'));
    } finally {
      setEditModalLoading(false);
    }
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEventData(null);
  };

  // Handle click outside to close event controls menu
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      // Check if the menu is open and the click is outside both the menu and the button
      if (
        showEventControlsMenu &&
        eventControlsMenuRef.current &&
        eventControlsMenuButtonRef.current &&
        !eventControlsMenuRef.current.contains(event.target as Node) &&
        !eventControlsMenuButtonRef.current.contains(event.target as Node)
      ) {
        setShowEventControlsMenu(null);
      }
    };

    // Only add event listener if menu is open
    if (showEventControlsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup function
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEventControlsMenu]);

  return (
    <>
      <div
        className={`feed-comments__comment feed-comments__comment--off-market-willingness ${!handleFeedEventClick ? 'feed-comments__comment--is-not-clickable feed-comments__comment--off-market-willingness--is-not-clickable' : ''}
        ${offMarketWillingnessEvent.isUserOwnEvent ? 'feed-comments__comment--off-market-willingness--is-user-own-event' : ''}`}
        onClick={() => handleFeedEventClick?.(offMarketWillingnessEvent)}
      >
        <div className="feed-comments__comment__header-and-content-wrapper">
          <div
            className="feed-comments__comment__commenter-order-number-and-timestamp-wrapper 
                        feed-comments__comment__commenter-order-number-and-timestamp-wrapper--no-text 
                         feed-comments__comment__commenter-order-number-and-timestamp-wrapper--could-sell
                        "
          >
            <div className="feed-comments__comment__commenter-order-number">
              <div className="feed-comments__comment__commenter-order-number__off-market-willingness-comment">
                {isCouldSellMessage ? (
                  <div className="feed-comments__comment__commenter-order-number__off-market-willingness-comment__could-sell-icon">
                    <OffMarketSellIcon />
                  </div>
                ) : (
                  <div className="feed-comments__comment__commenter-order-number__off-market-willingness-comment__could-buy-icon">
                    <OffMarketBuyIcon />
                  </div>
                )}
                <span>{shortAddress}</span>
              </div>
            </div>
{/*             {!isBuildingModal && (
              <div className="feed-comments__comment__timestamp">
                {getTimeAgo(offMarketWillingnessEvent.updated_at)}
              </div>
            )} */}
          </div>
          <div className="feed-comments__comment__content">
            <div
              className={`feed-comments__comment__content__text feed-comments__comment__content__text--off-market-willingness${isCouldSellMessage
                ? ''
                : ' feed-comments__comment__content__text--off-market-willingness--could-buy'
                }`}
            >
              {isCouldSellMessage ? (
                <>
                  <Trans i18nKey="feed.offMarketWillingnessEvent.sell.text" />
                </>
              ) : (
                <>
                  <Trans i18nKey="feed.offMarketWillingnessEvent.buy.text" />
                </>
              )}
            </div>

            {offMarketWillingnessEvent.images && offMarketWillingnessEvent.images.length > 0 && (
              <div className={`feed-comments__comment__content__property-details__images${hasPropertyDetails ? ' feed-comments__comment__content__property-details__images--has-property-details' : ''}${isBuildingModal ? ' feed-comments__comment__content__property-details__images--building-modal' : ''}`}>
                <ImageCarousel
                  images={offMarketWillingnessEvent.images}
                  isBuildingModal={isBuildingModal}
                  setShowImageModal={setShowImageModal}
                />
              </div>
            )}
            {hasPropertyDetails && (
              <div className="feed-comments__comment__content__property-details">
                {offMarketWillingnessEvent.property_description && (
                  <div className="feed-comments__comment__content__property-details__description">
                    {isBuildingModal || isDescriptionExpanded || (offMarketWillingnessEvent.property_description.length <= 110)
                      ?
                      <Linkify
                        options={{
                          attributes: {
                            rel: 'noopener noreferrer',
                            target: '_blank',
                          },
                        }}
                      >
                        {DOMPurify.sanitize(offMarketWillingnessEvent.property_description)}
                      </Linkify>
                      : <span className="feed-comments__comment__content__property-details__description-text">{
                        <Linkify
                          options={{
                            attributes: {
                              rel: 'noopener noreferrer',
                              target: '_blank',
                            },
                          }}
                        >
                          {DOMPurify.sanitize(offMarketWillingnessEvent.property_description.substring(0, 110))}
                        </Linkify>

                      }...</span>
                    }
                    {(!isBuildingModal && (offMarketWillingnessEvent.property_description.length > 110)) && (
                      <button
                        className="feed-comments__comment__content__property-details__show-toggle"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation(); // Prevent triggering parent click event
                          setIsDescriptionExpanded(!isDescriptionExpanded);
                        }}
                      >
                        {isDescriptionExpanded ? t('common.showLess', 'Show less') : t('common.showMore', 'Show more')}
                      </button>
                    )}
                  </div>
                )}
                <div className="feed-comments__comment__content__property-details__specs">
                  {offMarketWillingnessEvent.property_rooms && (
                    <div className="feed-comments__comment__content__property-details__spec">
                      <span className="feed-comments__comment__content__property-details__spec-icon">🏠</span>
                      <span>{offMarketWillingnessEvent.property_rooms} {t('building.offMarket.modal.rooms', { count: Number(offMarketWillingnessEvent.property_rooms) })}</span>
                    </div>
                  )}

                  {offMarketWillingnessEvent.property_size && (
                    <div className="feed-comments__comment__content__property-details__spec">
                      <span className="feed-comments__comment__content__property-details__spec-icon">📏</span>
                      <span>{offMarketWillingnessEvent.property_size} m²</span>
                    </div>
                  )}

                  {offMarketWillingnessEvent.property_floor !== undefined &&
                    offMarketWillingnessEvent.property_floor !== null && (
                      <div className="feed-comments__comment__content__property-details__spec">
                        <span className="feed-comments__comment__content__property-details__spec-icon">🔝</span>
                        <span>{offMarketWillingnessEvent.property_floor} {t('building.offMarket.modal.floor')}</span>
                      </div>
                    )}

                  {offMarketWillingnessEvent.property_renovation_status && (
                    <div className="feed-comments__comment__content__property-details__spec">
                      <span className="feed-comments__comment__content__property-details__spec-icon">🔨</span>
                      <span>{getRenovationStatusText(offMarketWillingnessEvent.property_renovation_status)}</span>
                    </div>
                  )}

                  {offMarketWillingnessEvent.property_has_elevator && (
                    <div className="feed-comments__comment__content__property-details__spec">
                      <span className="feed-comments__comment__content__property-details__spec-icon">⬆️</span>
                      <span>{t('building.offMarket.modal.hasElevator')}</span>
                    </div>
                  )}

                  {offMarketWillingnessEvent.property_has_balcony && (
                    <div className="feed-comments__comment__content__property-details__spec">
                      <span className="feed-comments__comment__content__property-details__spec-icon">🏞️</span>
                      <span>{t('building.offMarket.modal.hasBalcony')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        {offMarketWillingnessEvent.contractorLogoUrl && (
          <div className={`feed-comments__comment__content__contractor-logo${hasPropertyDetails ? ' feed-comments__comment__content__contractor-logo--has-property-details' : ''}`}>
            <img src={offMarketWillingnessEvent.contractorLogoUrl} alt="Contractor logo" />
          </div>
        )}
        {showEventControlsMenu === offMarketWillingnessEvent._id && (
          <div
            className="feed-comments__comment__off-market-willingness-event__footer__menu-wrapper"
            ref={eventControlsMenuRef}
          >
            <div
              className="feed-comments__comment__off-market-willingness-event__footer__menu"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <button
                className={`feed-comments__comment__off-market-willingness-event__footer__menu__edit-event-button`}
                ref={editEventRef}
                onClick={handleEditClick}
              >
                <div className="feed-comments__comment__off-market-willingness-event__footer__menu__edit-event-button__text">
                  <svg
                    height="16px"
                    viewBox="0 0 492.49284 492"
                    width="16px"
                    xmlns="http://www.w3.org/2000/svg"
                    id="fi_1828911">
                    <path
                      d="m304.140625 82.472656-270.976563 270.996094c-1.363281 1.367188-2.347656 3.09375-2.816406 4.949219l-30.035156 120.554687c-.898438 3.628906.167969 7.488282 2.816406 10.136719 2.003906 2.003906 4.734375 3.113281 7.527344 3.113281.855469 0 1.730469-.105468 2.582031-.320312l120.554688-30.039063c1.878906-.46875 3.585937-1.449219 4.949219-2.8125l271-270.976562zm0 0"></path><path d="m476.875 45.523438-30.164062-30.164063c-20.160157-20.160156-55.296876-20.140625-75.433594 0l-36.949219 36.949219 105.597656 105.597656 36.949219-36.949219c10.070312-10.066406 15.617188-23.464843 15.617188-37.714843s-5.546876-27.648438-15.617188-37.71875zm0 0"
                      >
                    </path>
                  </svg>
                  <Trans i18nKey="building.offMarket.modal.edit.menuTitle" />
                </div>
              </button>
              <button
                className={`feed-comments__comment__off-market-willingness-event__footer__menu__edit-event-button feed-comments__comment__off-market-willingness-event__footer__menu__edit-event-button--warning`}
                onClick={() => {
                  setShowDeleteConfirm(true);
                  setShowEventControlsMenu(null);
                }}
              >
                <div className="feed-comments__comment__off-market-willingness-event__footer__menu__edit-event-button__text">
                  <TrashIcon color="#202125" size="16px" />
                  <Trans i18nKey="building.offMarket.modal.remove.menuTitle">Remove</Trans>
                </div>
              </button>
            </div>
          </div>
        )}
        <hr className="feed-comments__comment__off-market-willingness-event__divider" />
        <div className={`feed-comments__comment__off-market-willingness-event__footer${hasPropertyDetails || (offMarketWillingnessEvent.images && offMarketWillingnessEvent.images.length > 0) ? ' feed-comments__comment__off-market-willingness-event__footer--has-details-or-images' : ''}`}>
          <button
            className={`feed-comments__comment__off-market-willingness-event__footer__share-button${hasPropertyDetails || (offMarketWillingnessEvent.images && offMarketWillingnessEvent.images.length > 0) ? ' feed-comments__comment__off-market-willingness-event__footer__share-button--has-details-or-images' : ''}`}
            onClick={async (e) => {
              e.stopPropagation();
              e.preventDefault();

              TagManager.dataLayer({
                dataLayer: {
                  event: `${offMarketWillingnessEvent.could_sell ? 'feed_event_could_sell_shared' : 'feed_event_could_buy_shared'}`,
                  category: 'User Interaction',
                  action: 'Click',
                  label: 'Share',
                  event_id: offMarketWillingnessEvent._id,
                  event_address: offMarketWillingnessEvent.address.street + ' ' + offMarketWillingnessEvent.address.houseNumber + ', ' + offMarketWillingnessEvent.address.postcode + ' ' + offMarketWillingnessEvent.address.city,
                },
              });

              // Use backend share endpoint for social media crawlers to get proper meta tags
              // The backend will redirect regular users to the main app with the same parameters
              const shareUrl = `${process.env.REACT_APP_API_URL}/share-event?building-id=${offMarketWillingnessEvent.buildingId}&event-id=${offMarketWillingnessEvent._id}`;
              
              const shareTitle = offMarketWillingnessEvent.could_sell
                ? t('building.offMarket.shareTitle.couldSell')
                : t('building.offMarket.shareTitle.couldBuy');
              const shareText = `${shareTitle} - ${offMarketWillingnessEvent.address.street} ${offMarketWillingnessEvent.address.houseNumber}, ${offMarketWillingnessEvent.address.postcode} ${offMarketWillingnessEvent.address.city}`;

              if (navigator.share) {
                try {
                  await navigator.share({
                    title: shareTitle,
                    text: shareText,
                    url: shareUrl,
                  });
                } catch (error) {
                  console.error('Error sharing:', error);
                }
              } else if (navigator.clipboard && navigator.clipboard.writeText) {
                // Fallback to copying to clipboard
                try {
                  await navigator.clipboard.writeText(shareUrl);
                  alert(t('shareButton.linkCopied', 'Link copied to clipboard'));
                } catch (err) {
                  console.error('Could not copy text: ', err);
                  alert(t('shareButton.linkCopiedError', 'Could not copy link'));
                }
              } else {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = shareUrl;
                textArea.style.position = 'fixed';
                textArea.style.top = '0';
                textArea.style.left = '0';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();

                try {
                  document.execCommand('copy');
                  alert(t('shareButton.linkCopied', 'Link copied to clipboard'));
                } catch (err) {
                  console.error('Fallback: Unable to copy', err);
                  alert(t('shareButton.linkCopiedError', 'Could not copy link'));
                }

                document.body.removeChild(textArea);
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
              viewBox="0,0,256,256"
              width="20px"
              height="20px"
              fillRule="nonzero"
            >
              <g
                fill="#333"
                fillRule="nonzero"
                stroke="none"
                strokeWidth="1"
                strokeLinecap="butt"
                strokeLinejoin="miter"
                strokeMiterlimit="10"
                strokeDasharray=""
                strokeDashoffset="0"
                fontFamily="none"
                fontWeight="none"
                fontSize="none"
                textAnchor="none"
                style={{ mixBlendMode: 'normal' }}
              >
                <g transform="scale(8.53333,8.53333)">
                  <path d="M23,3c-2.20914,0 -4,1.79086 -4,4c0.00178,0.28117 0.03321,0.56136 0.09375,0.83594l-9.08203,4.54102c-0.75785,-0.87251 -1.85604,-1.3746 -3.01172,-1.37695c-2.20914,0 -4,1.79086 -4,4c0,2.20914 1.79086,4 4,4c1.15606,-0.0013 2.25501,-0.5027 3.01367,-1.375l9.07617,4.53906c-0.05923,0.27472 -0.08934,0.55491 -0.08984,0.83594c0,2.20914 1.79086,4 4,4c2.20914,0 4,-1.79086 4,-4c0,-2.20914 -1.79086,-4 -4,-4c-1.15606,0.0013 -2.25501,0.5027 -3.01367,1.375l-9.07617,-4.53906c0.05923,-0.27472 0.08934,-0.55491 0.08984,-0.83594c-0.00192,-0.28051 -0.03334,-0.56005 -0.09375,-0.83398l9.08203,-4.54102c0.75821,0.87178 1.85635,1.37313 3.01172,1.375c2.20914,0 4,-1.79086 4,-4c0,-2.20914 -1.79086,-4 -4,-4z"></path>
                </g>
              </g>
            </svg>
            <div className="feed-comments__comment__off-market-willingness-event__footer__share-button__text">
              <Trans i18nKey="building.controls.share" />
            </div>
          </button>
          {offMarketWillingnessEvent.isUserOwnEvent && (
            <button
              className={`feed-comments__comment__off-market-willingness-event__footer__menu__show-menu-button${hasPropertyDetails || (offMarketWillingnessEvent.images && offMarketWillingnessEvent.images.length > 0) ? ' feed-comments__comment__off-market-willingness-event__footer__menu__show-menu-button--has-details-or-images' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (showEventControlsMenu === offMarketWillingnessEvent._id) {
                  setShowEventControlsMenu(null);
                } else {
                  setShowEventControlsMenu(offMarketWillingnessEvent._id);
                }
              }}
              ref={eventControlsMenuButtonRef}
            >
              <MiniMenuIcon color="#333" size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <OffMarketBuyAndSellDetailsModal
          isOpen={isEditModalOpen}
          onClose={handleEditModalClose}
          onConfirm={handleEditConfirm}
          actionType={offMarketWillingnessEvent.could_sell ? 'sell' : 'buy'}
          isLoading={editModalLoading}
          isEditMode={true}
          initialData={eventData || undefined}
          willingnessId={offMarketWillingnessEvent._id}
          setIsEditModalLoading={setEditModalLoading}
        />
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="end-chat-confirmation-overlay" onClick={() => !isDeleting && setShowDeleteConfirm(false)}>
          <div className="end-chat-confirmation" onClick={(e) => e.stopPropagation()}>
            <div className="end-chat-confirmation__header">
              <h3>
                <Trans i18nKey="building.offMarket.modal.deleteConfirmTitle">Remove listing</Trans>
              </h3>
              <button
                className="end-chat-confirmation__close"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                <CloseIcon color="#202125" size={16} />
              </button>
            </div>
            <div className="end-chat-confirmation__content">
              <p>
                <Trans i18nKey="building.offMarket.modal.deleteConfirmText">Are you sure you want to remove this event?</Trans>
              </p>
            </div>
            <div className="end-chat-confirmation__actions">
              <button
                className="end-chat-confirmation__button end-chat-confirmation__button--cancel"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                <Trans i18nKey="building.offMarket.modal.cancel">Cancel</Trans>
              </button>
              <button
                className="end-chat-confirmation__button end-chat-confirmation__button--confirm"
                onClick={async () => {
                  try {
                    setIsDeleting(true);
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/off-market-willingness/${offMarketWillingnessEvent._id}`,
                      {
                        method: 'DELETE',
                        credentials: 'include',
                        headers: {
                          'Content-Type': 'application/json',
                          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
                        },
                      });
                    if (!response.ok) {
                      throw new Error('Failed to delete');
                    }
                    setShowDeleteConfirm(false);
                    fetchFeed?.();
                    fetchComments?.(false);
                    handleRefreshFeedClick();
                    if (offMarketWillingnessEvent.could_sell) {
                      setCouldSellSelected?.(false);
                    } else {
                      setCouldBuySelected?.(false);
                    }
                  } catch (err) {
                    console.error('Error deleting willingness:', err);
                    alert(t('common.error', 'An error occurred. Please try again.'));
                  } finally {
                    setIsDeleting(false);
                  }
                }}
                disabled={isDeleting}
              >
                {isDeleting ? t('building.offMarket.modal.loading', 'Loading...') : <Trans i18nKey="building.offMarket.modal.delete">Remove</Trans>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
